import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Code, Webhook, Copy, Check, Lock, Trash2 } from 'lucide-react';
import axios from "axios";
import { toast } from 'react-hot-toast';

axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:5000"; // Backend URL

const IntegrationsSection = ({ usageStats, templates }) => {
  console.log("Usage Stats in IntegrationsSection:", usageStats);
  const integrationsEnabled = usageStats?.month?.limit > 500;

  const [webhooks, setWebhooks] = useState([]);
  const [newWebhook, setNewWebhook] = useState({ url: '', templateId: '', secret: '' });
  const [copied, setCopied] = useState('');

  // Since usage stats are already fetched in Dashboard, no need to re-fetch here.
  // If necessary, you can still fetch webhooks from a dedicated endpoint.
  const fetchWebhooks = async () => {
    try {
      const response = await axios.get('/api/webhooks');
      setWebhooks(response.data.webhooks);
    } catch (error) {
      console.error('Error fetching webhooks:', error);
    }
  };

  // Fetch webhooks only once if integration is enabled
  if (integrationsEnabled && webhooks.length === 0) {
    fetchWebhooks();
  }

  const handleCopy = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleSaveWebhook = async () => {
    try {
      await axios.post('/api/webhooks', newWebhook);
      toast.success('Webhook saved successfully');
      fetchWebhooks();
      setNewWebhook({ url: '', templateId: '', secret: '' });
    } catch (error) {
      toast.error('Failed to save webhook');
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    try {
      await axios.delete(`/api/webhooks/${webhookId}`);
      toast.success('Webhook deleted successfully');
      fetchWebhooks();
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  // API Code Examples -----------------------------
  const apiExamples = {
    curl: `curl -X POST \\
  'https://api.clevrscan.com/api/upload_ocr_sync' \\
  -H 'Authorization: Bearer YOUR_API_KEY' \\
  -F 'files=@document.pdf' \\
  -F 'scan_type=invoice' \\
  -F 'series=INV001'`,
    
    python: `import requests

url = 'https://api.clevrscan.com/api/upload_ocr_sync'
headers = {
    'Authorization': 'Bearer YOUR_API_KEY'
}
files = {
    'files': open('document.pdf', 'rb')
}
data = {
    'scan_type': 'invoice',
    'series': 'INV001'
}

response = requests.post(url, headers=headers, files=files, data=data)
print(response.json())`,
    
    javascript: `const formData = new FormData();
formData.append('files', file);
formData.append('scan_type', 'invoice');
formData.append('series', 'INV001');

const response = await fetch('https://api.clevrscan.com/api/upload_ocr_sync', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY'
  },
  body: formData
});

const result = await response.json();`
  };

  if (!integrationsEnabled) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] p-8 space-y-6">
        <div className="relative">
          <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg blur opacity-25"></div>
          <div className="relative bg-gray-900 p-8 rounded-lg border border-gray-800 shadow-2xl">
            <Lock className="w-12 h-12 text-indigo-400 mb-4" />
            <h3 className="text-2xl font-bold text-white mb-4">Unlock Advanced Integrations</h3>
            <p className="text-gray-400 mb-6">
              Upgrade your plan to increase your monthly scan limit and unlock powerful API and webhook integrations.
            </p>
            <button
              onClick={() => {/* Add your upgrade flow here */}}
              className="w-full px-6 py-3 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white font-medium transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="border-b border-gray-800 pb-6">
        <h2 className="text-3xl font-bold text-white mb-4">API & Webhooks</h2>
        <p className="text-gray-400">
          Integrate ClevrScan's powerful OCR capabilities directly into your applications.
        </p>
      </div>

      {/* API Documentation Section */}
      <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
        <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
          <Code className="mr-2" size={20} />
          API Documentation
        </h3>
        <div className="space-y-6">
          {/* Display API Key */}
          <div className="bg-gray-800 p-4 rounded-lg">
            <p className="text-sm text-gray-400 mb-2">Your API Key</p>
            <div className="flex items-center space-x-2">
              <code className="flex-1 bg-gray-900 px-3 py-2 rounded text-indigo-400 font-mono">
                {/* You can reuse the API key from your usage stats or a similar source */}
                YOUR_API_KEY
              </code>
              <button
                onClick={() => handleCopy('YOUR_API_KEY', 'apiKey')}
                className="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300"
              >
                {copied === 'apiKey' ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
          </div>

          {/* Sync OCR API Code Examples */}
          <div className="bg-gray-800 p-4 rounded-lg">
            <h4 className="text-white font-medium mb-2">Sync OCR Upload API</h4>
            <p className="text-gray-400 text-sm mb-4">Upload and process documents synchronously</p>
            <div className="space-y-4">
              {Object.entries(apiExamples).map(([lang, code]) => (
                <div key={lang} className="relative">
                  <div className="absolute top-2 right-2 flex items-center space-x-2">
                    <span className="text-xs text-gray-500 uppercase">{lang}</span>
                    <button
                      onClick={() => handleCopy(code, lang)}
                      className="p-1 hover:bg-gray-700 rounded"
                    >
                      {copied === lang ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                  <pre className="bg-gray-900 p-4 rounded-lg overflow-x-auto">
                    <code className="text-indigo-400 text-sm">{code}</code>
                  </pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Webhooks Section */}
      <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
        <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
          <Webhook className="mr-2" size={20} />
          Template Webhooks
        </h3>
        {/* Add New Webhook */}
        <div className="bg-gray-800 p-4 rounded-lg mb-6">
          <h4 className="text-white font-medium mb-4">Add New Webhook</h4>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 block mb-1">Template</label>
              <select
                value={newWebhook.templateId}
                onChange={(e) => setNewWebhook({ ...newWebhook, templateId: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
              >
                <option value="">Select Template</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 block mb-1">Webhook URL</label>
              <input
                type="url"
                value={newWebhook.url}
                onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                placeholder="https://your-domain.com/webhook"
              />
            </div>
            <button
              onClick={handleSaveWebhook}
              className="w-full px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg"
            >
              Add Webhook
            </button>
          </div>
        </div>

        {/* Existing Webhooks List */}
        <div className="space-y-4">
          {webhooks.map((webhook) => (
            <div key={webhook.id} className="bg-gray-800 p-4 rounded-lg flex items-center justify-between">
              <div>
                <p className="text-white font-medium">{webhook.template.name}</p>
                <p className="text-gray-400 text-sm">{webhook.url}</p>
              </div>
              <button
                onClick={() => handleDeleteWebhook(webhook.id)}
                className="p-2 hover:bg-gray-700 rounded-lg text-red-400"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IntegrationsSection; 